import {
  CheckCircle,
  ChevronLeft,
  CircleNotifications,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useState } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";

import {
  CampaignStatus,
  EnqueueTexterSendMutation,
  EnqueueTexterSendMutationVariables,
  InboxType,
  ReleaseAssignmentsMutation,
  ReleaseAssignmentsMutationVariables,
  RequestAssignmentBatchMutation,
  RequestAssignmentBatchMutationVariables,
  TexterCampaignSendStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink, LinkButton } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table from "~frontend/components/Table";
import {
  RELEASE_ASSIGNMENTS,
  REQUEST_ASSIGNMENT_BATCH,
} from "~frontend/graphql/assignments";
import { ENQUEUE_TEXTER_SEND } from "~frontend/graphql/campaigns";
import { GET_INBOX } from "~frontend/graphql/inboxes";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { InboxCampaignSummary } from "./InboxCampaignSummary";
import { ReplyControls } from "./ReplyControls";
import { GeneralInboxDetails, EscalationInboxDetails } from "./types";

export function NonCampaignInbox({
  inbox,
}: {
  inbox: GeneralInboxDetails | EscalationInboxDetails;
}): ReactElement {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { subaccountLink, subaccount } = useSubaccount()!;

  useBreadcrumbs(
    [
      {
        label: subaccount.name,
        path: subaccountLink("/inboxes"),
        backArrow: true,
      },
      {
        label: inbox.inboxName!,
        path: subaccountLink(`/inboxes/${inbox.id}`),
      },
    ],
    { hideSubaccountTitle: true }
  );

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);

  const getCampaignLink = useCallback(
    ({ campaignId }: { campaignId: string | null }) => {
      return subaccountLink(
        `/inboxes/${inbox.id}/campaigns/${campaignId ?? "no_campaign"}`
      );
    },
    [subaccountLink, inbox]
  );

  const [requestAssignmentBatch, { loading: requestAssignmentBatchLoading }] =
    useMutation<
      RequestAssignmentBatchMutation,
      RequestAssignmentBatchMutationVariables
    >(REQUEST_ASSIGNMENT_BATCH, {
      refetchQueries: [GET_INBOX],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error claiming replies",
        description: "Error message for claiming replies",
      }),
    });

  const [releaseAssignments, { loading: releaseAssignmentsLoading }] =
    useMutation<
      ReleaseAssignmentsMutation,
      ReleaseAssignmentsMutationVariables
    >(RELEASE_ASSIGNMENTS, {
      refetchQueries: [GET_INBOX],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error releasing replies",
        description: "Error message for releasing replies",
      }),
    });

  const assignmentRequestInProgress =
    requestAssignmentBatchLoading || releaseAssignmentsLoading;

  const handleRequestAssignmentBatch = useCallback(
    async ({ campaignId }: { campaignId: string | null }) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const requestAssignmentBatchResponse = await requestAssignmentBatch({
        variables: { input: { inboxId: inbox.id, campaignId } },
      });

      if (
        requestAssignmentBatchResponse.errors ||
        !requestAssignmentBatchResponse.data
      ) {
        console.error(requestAssignmentBatchResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error claiming replies",
            description: "Error message for claiming replies",
          })
        );
        return;
      }
    },
    [assignmentRequestInProgress, requestAssignmentBatch, inbox, snackbar, intl]
  );

  const handleReleaseAssignments = useCallback(
    async ({ campaignId }: { campaignId?: string | null }) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const releaseAssignmentsResponse = await releaseAssignments({
        variables: {
          input: { subaccountId: subaccount.id, inboxId: inbox.id, campaignId },
        },
      });

      if (
        releaseAssignmentsResponse.errors ||
        !releaseAssignmentsResponse.data
      ) {
        console.error(releaseAssignmentsResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error releasing replies",
            description: "Error message for releasing replies",
          })
        );
        return;
      }
    },
    [
      assignmentRequestInProgress,
      releaseAssignments,
      subaccount,
      inbox,
      snackbar,
      intl,
    ]
  );

  const [enqueuingTexterSendCampaignIds, setEnqueuingTexterSendCampaignIds] =
    useState<string[]>([]);

  const [enqueueTexterSend] = useMutation<
    EnqueueTexterSendMutation,
    EnqueueTexterSendMutationVariables
  >(ENQUEUE_TEXTER_SEND, {
    refetchQueries: [GET_INBOX],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Error sending initials",
      description: "Error message for sending initial messages",
    }),
  });

  const handleEnqueueTexterSend = useCallback(
    async (campaignId: string) => {
      if (enqueuingTexterSendCampaignIds.includes(campaignId)) {
        return;
      }

      setEnqueuingTexterSendCampaignIds([
        ...enqueuingTexterSendCampaignIds,
        campaignId,
      ]);

      try {
        const { data, errors } = await enqueueTexterSend({
          variables: { input: { campaignId } },
        });

        if (errors || !data) {
          snackbar.enqueueSnackbar(
            intl.formatMessage({
              defaultMessage: "Error sending initials",
              description: "Error message for sending initial messages",
            })
          );
        }
      } finally {
        setEnqueuingTexterSendCampaignIds(
          enqueuingTexterSendCampaignIds.filter(cid => cid !== campaignId)
        );
      }
    },
    [
      enqueuingTexterSendCampaignIds,
      setEnqueuingTexterSendCampaignIds,
      enqueueTexterSend,
      snackbar,
      intl,
    ]
  );

  const {
    numRepliesAvailable: totalAvailable,
    numRepliesAssigned: totalAssigned,
  } = inbox.stats;

  return (
    <PageContainer maxWidth="md">
      <InternalLink
        to={subaccountLink(`/inboxes`)}
        sx={{ textDecoration: "none", color: palette.darkGray }}
      >
        <FlexBox alignItems="center">
          {/*Use a negative value to cut off padding from SVG.*/}
          <ChevronLeft sx={{ marginLeft: -1 }} />
          <Typography variant="body2Bold">
            <FormattedMessage
              defaultMessage="Back to Your Inboxes"
              description="Back to inboxes link label"
            />
          </Typography>
        </FlexBox>
      </InternalLink>
      <FlexBox justifyContent="space-between">
        <PageHeader>{inbox.inboxName!}</PageHeader>
        <FlexBox flexDirection="column" alignItems="flex-end">
          <FlexBox alignItems="center" gap="6px">
            <Typography variant="body1Bold">
              {inbox.inboxType === InboxType.General ? (
                <FormattedMessage
                  defaultMessage="{totalAssigned, plural, =1 {1 Reply} other {Your Replies: {totalAssigned}}}"
                  description="Alert for the number of assignments"
                  values={{ totalAssigned }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="{totalAvailable, plural, =1 {1 Escalation} other {{totalAvailable} Escalations}}"
                  description="Alert for the number of escalations"
                  values={{ totalAvailable }}
                />
              )}
            </Typography>
            {totalAvailable > 0 ? (
              <CircleNotifications color="error" sx={{ width: "18px" }} />
            ) : (
              <CheckCircle color="success" sx={{ width: "18px" }} />
            )}
          </FlexBox>
          <InternalLink to={subaccountLink(`/inboxes/${inbox.id}/campaigns`)}>
            <Typography variant="body3" sx={{ display: "block" }}>
              <FormattedMessage
                defaultMessage="View all your replies for this inbox"
                description="Button text for viewing all replies"
              />
            </Typography>
          </InternalLink>
        </FlexBox>
      </FlexBox>
      <Box
        sx={{
          backgroundColor: palette.white,
          boxShadow: `0px 1px 5px 0px #6A678333`,
          padding: "40px",
          marginTop: theme.spacing(2),
        }}
      >
        <Table
          rows={inbox.statsByCampaign
            .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
            .map(statsByCampaign => ({
              // table row ids must be strings, so we need to handle
              // null campaign id stats
              id: statsByCampaign.campaign?.id ?? "null",
              statsByCampaign,
            }))}
          columns={[
            {
              key: "campaign",
              title: intl.formatMessage({
                defaultMessage: "Campaign",
                description: "Header for campaign column",
              }),
              renderValue: ({ statsByCampaign }) => {
                switch (inbox.inboxType) {
                  case InboxType.General:
                    return (
                      <InboxCampaignSummary
                        campaign={statsByCampaign.campaign}
                        showTexterSendInfo={true}
                      />
                    );
                  case InboxType.Escalation:
                    return (
                      <InboxCampaignSummary
                        campaign={statsByCampaign.campaign}
                        showTexterSendInfo={false}
                      />
                    );
                  default:
                    return (<></>) as never;
                }
              },
            },
            {
              key: "total-replies",
              title:
                inbox.inboxType === InboxType.General
                  ? intl.formatMessage({
                      defaultMessage: "Available Replies",
                      description: "Header for available escalations column",
                    })
                  : intl.formatMessage({
                      defaultMessage: "Available Escalations",
                      description: "Header for available escalations column",
                    }),
              renderValue: ({ statsByCampaign }) => (
                <FormattedNumber value={statsByCampaign.availableCount} />
              ),
            },
            {
              key: "your-replies",
              title:
                inbox.inboxType === InboxType.General
                  ? intl.formatMessage({
                      defaultMessage: "Your Replies",
                      description: "Header for the your replies column",
                    })
                  : intl.formatMessage({
                      defaultMessage: "Your Escalations",
                      description: "Header for the your replies column",
                    }),
              renderValue: ({ statsByCampaign }) => (
                <FormattedNumber value={statsByCampaign.assignmentCount} />
              ),
            },
            {
              key: "actions",
              title: intl.formatMessage({
                defaultMessage: "Actions",
                description: "Header for the actions column",
              }),
              renderValue: ({ statsByCampaign }) => {
                const canSendInitials =
                  inbox.__typename === "GeneralInboxDetails"
                    ? statsByCampaign.campaign &&
                      !subaccount.sendingDisabled &&
                      statsByCampaign.campaign?.texterSendEnabled &&
                      statsByCampaign.campaign?.status ===
                        CampaignStatus.InitialSendRunning &&
                      statsByCampaign.campaign?.withinTextingHours
                    : false;

                const enqueuingSendForThisCampaign =
                  enqueuingTexterSendCampaignIds.includes(
                    statsByCampaign.campaign?.id ?? "null"
                  );
                const hasPendingTexterSend =
                  statsByCampaign.campaign?.myCampaignSends.some(
                    send => send.status === TexterCampaignSendStatus.Pending
                  ) ?? false;

                return (
                  <ReplyControls
                    assignmentCount={statsByCampaign.assignmentCount}
                    availableCount={statsByCampaign.availableCount}
                    assignmentNeedsReplyCount={
                      statsByCampaign.assignmentNeedsReplyCount
                    }
                    canSendInitials={canSendInitials ?? false}
                    texterSendInProgress={
                      enqueuingSendForThisCampaign || hasPendingTexterSend
                    }
                    getCampaignLink={() =>
                      getCampaignLink({
                        campaignId: statsByCampaign.campaign?.id ?? null,
                      })
                    }
                    texterSendBatchSize={
                      statsByCampaign.campaign?.texterSendBatchSize ?? null
                    }
                    handleEnqueueTexterSend={() =>
                      handleEnqueueTexterSend(statsByCampaign.campaign!.id)
                    }
                    handleReleaseAssignments={() =>
                      handleReleaseAssignments({
                        campaignId: statsByCampaign.campaign?.id,
                      })
                    }
                    handleRequestAssignmentBatch={() =>
                      handleRequestAssignmentBatch({
                        campaignId: statsByCampaign.campaign?.id ?? null,
                      })
                    }
                  />
                );
              },
            },
          ]}
          totalCount={inbox.statsByCampaign.length}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={setRowsPerPage}
          page={page}
          onChangePage={setPage}
          noFooter={inbox.statsByCampaign.length === 0}
          footerActions={
            <LinkButton
              variant="body2"
              onClick={() => handleReleaseAssignments({})}
              sx={{ display: totalAssigned === 0 ? "none" : "auto" }}
            >
              <FormattedMessage
                defaultMessage="Release All My Threads"
                description="Button text for releasing all threads"
              />
            </LinkButton>
          }
          reverseFooterOrder
          emptyState={
            inbox.inboxType === InboxType.Escalation
              ? intl.formatMessage({
                  defaultMessage: "There are no escalations in this inbox",
                  description: "Message for an empty escalation inbox",
                })
              : intl.formatMessage({
                  defaultMessage:
                    "There are no available replies in this inbox",
                  description: "Message for an empty general inbox",
                })
          }
        />
      </Box>
    </PageContainer>
  );
}
